import React, { useState } from "react";
import {
  Box, Button, Card, Typography,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,  Table, TableHead, TableRow, TableCell, TableBody,
  TextField, CircularProgress, Skeleton, TablePagination,
  Snackbar, Alert, InputAdornment, Tooltip, Paper, Grid
} from "@mui/material";
import { Plus, Pencil, Trash2, X, Layers, Type, AlignLeft, Hash, Search } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import {
  adminGetResourceCategories,
  adminGetResourceCategoryById,
  adminCreateResourceCategory,
  adminUpdateResourceCategory,
  adminDeleteResourceCategory,
} from "../../api/resources";

// ── Helpers ─────────────────────────────────────────────────────────────────
const slugify = (str) =>
  str.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const DEFAULT_VALUES = {
  name: "",
  slug: "",
  description: "",
  order: 0,
};

export default function ResourceCategoriesPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const watchName = watch("name", "");
  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // ── Queries with search & pagination ─────────────────────────────────────
  const { data: apiCategories = [], isLoading } = useQuery({
    queryKey: ["admin-resource-categories"],
    queryFn: () => adminGetResourceCategories().then(r => r.data),
  });

  const categories = Array.isArray(apiCategories) ? apiCategories : [];
  const filteredCategories = categories.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const paginatedCategories = filteredCategories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalCount = filteredCategories.length;

  const createMutation = useMutation({
    mutationFn: adminCreateResourceCategory,
    onSuccess: () => {
      qc.invalidateQueries(["admin-resource-categories"]);
      notify("Category created");
      closeDialog();
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't create category", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateResourceCategory(id, data),
    onSuccess: () => {
      qc.invalidateQueries(["admin-resource-categories"]);
      notify("Category updated");
      closeDialog();
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update category", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeleteResourceCategory,
    onSuccess: () => {
      qc.invalidateQueries(["admin-resource-categories"]);
      notify("Category deleted");
      setConfirmDelete(null);
    },
    onError: (err) => {
      notify(err?.response?.data?.message || "Couldn't delete category", "error");
      setConfirmDelete(null);
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  // ── Form Submit (JSON payload - NOT multipart) ────────────────────────────
  const onSubmit = (values) => {
    const payload = {
      name: values.name,
      slug: values.slug?.trim() || slugify(values.name),
      description: values.description || "",
      order: values.order !== "" && values.order != null ? Number(values.order) : 0,
    };

    if (editing) updateMutation.mutate({ id: editing.id, data: payload });
    else createMutation.mutate(payload);
  };

  const openCreate = () => {
    setEditing(null);
    reset(DEFAULT_VALUES);
    setOpen(true);
  };

  const openEdit = async (c) => {
    setEditLoading(true);
    try {
      const { data } = await adminGetResourceCategoryById(c.id);
      setEditing(data);
      reset({
        name: data.name ?? "",
        slug: data.slug ?? "",
        description: data.description ?? "",
        order: data.order ?? 0,
      });
      setOpen(true);
    } catch (err) {
      notify(err?.response?.data?.message || "Couldn't load category details", "error");
    } finally {
      setEditLoading(false);
    }
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false);
    setEditing(null);
    reset(DEFAULT_VALUES);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  return (
    <AdminShell title="Resource Categories">
      {/* Page header */}
      <Box sx={{
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5, mb: 2.5,
      }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
          Categories used to organize resources in the knowledge center.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{
            borderRadius: "10px", textTransform: "none", fontWeight: 700,
            bgcolor: "#2563EB", boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)",
            flexShrink: 0, "&:hover": { bgcolor: "#1D4ED8" },
          }}
        >
          Add Category
        </Button>
      </Box>

      {/* Search Bar */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        p: 2, mb: 2.5,
      }}>
        <TextField
          fullWidth
          placeholder="Search categories by name, slug, or description..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#94A3B8" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              border: "1px solid rgba(15,23,42,0.15)",
              "&:hover": { borderColor: "#2563EB" },
            },
          }}
        />
      </Card>

      {/* Table Card */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        overflow: "hidden"
      }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Name", "Slug", "Items", "Order", ""].map(h => (
                  <TableCell key={h} sx={{
                    fontWeight: 700, fontSize: 12, letterSpacing: "0.03em",
                    textTransform: "uppercase", color: "text.secondary",
                    borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap"
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 180 : 80} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <Box sx={{
                      width: 52, height: 52, borderRadius: "14px",
                      bgcolor: "rgba(37,99,235,0.07)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto", mb: 1.5
                    }}>
                      <Layers size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>
                      No categories found
                    </Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery ? "Try adjusting your search" : "Create categories to organize your resources."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedCategories.map((c) => (
                <TableRow key={c.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{
                        width: 34, height: 34, borderRadius: "9px",
                        bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0
                      }}>
                        <Layers size={15} strokeWidth={2.1} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A", lineHeight: 1.3 }} noWrap>
                          {c.name}
                        </Typography>
                        {c.description && (
                          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.2 }} noWrap>
                            {c.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ maxWidth: 180 }}>
                    <Typography sx={{
                      fontSize: 12.5, fontFamily: "monospace", color: "rgba(15,23,42,0.5)",
                      bgcolor: "rgba(15,23,42,0.04)", px: 1, py: 0.35, borderRadius: "6px",
                      display: "inline-block", maxWidth: 160, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap"
                    }}>
                      {c.slug || "—"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ fontSize: 13.5, color: "text.secondary", whiteSpace: "nowrap" }}>
                    {c.item_count ?? "—"}
                  </TableCell>

                  <TableCell sx={{ fontSize: 13.5, color: "text.secondary", whiteSpace: "nowrap" }}>
                    {c.order ?? 0}
                  </TableCell>

                  <TableCell align="right">
                    <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                      <Tooltip title="Edit">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => openEdit(c)}
                            disabled={editLoading}
                            sx={{
                              border: "1px solid rgba(15,23,42,0.1)",
                              borderRadius: "8px",
                              "&:hover": { borderColor: "#2563EB", color: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" }
                            }}
                          >
                            {editLoading ? <CircularProgress size={14} /> : <Pencil size={14} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => setConfirmDelete(c)}
                          sx={{
                            border: "1px solid rgba(15,23,42,0.1)",
                            borderRadius: "8px",
                            "&:hover": { borderColor: "#EF4444", color: "#EF4444", bgcolor: "rgba(239,68,68,0.05)" }
                          }}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Pagination Controls */}
        {!isLoading && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid rgba(15,23,42,0.08)",
              "& .MuiTablePagination-toolbar": { padding: "16px 24px" },
              "& .MuiTablePagination-selectLabel": {
                fontSize: "13.5px", color: "text.secondary", marginRight: "8px",
              },
              "& .MuiTablePagination-displayedRows": { fontSize: "13.5px", color: "text.secondary" },
              "& .MuiInputBase-root": {
                borderRadius: "8px", border: "1px solid rgba(15,23,42,0.15)",
              },
              "& .MuiTablePagination-actions": { gap: "4px" },
              "& .MuiIconButton-root": {
                borderRadius: "8px", border: "1px solid rgba(15,23,42,0.1)", width: "32px", height: "32px",
              },
            }}
          />
        )}
      </Card>

      {/* Enhanced Create/Edit Dialog - BLUE GRADIENT (consistent with other pages) */}
      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            maxHeight: "90vh",
            overflow: "hidden",
            boxShadow: "0 30px 70px rgba(15,23,42,0.22)",
          }
        }}
      >
        <DialogTitle sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2.5,
          px: 3,
          background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 55%, #3B82F6 100%)",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <Layers size={22} color="#FFFFFF" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 21, color: "#FFFFFF", letterSpacing: "-0.01em" }}>
                {editing ? "Edit Category" : "Add New Category"}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.85)", fontWeight: 500, mt: 0.25 }}>
                {editing ? `Editing: ${editing.name}` : "Create a new category for your knowledge center"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            pt: 3,
            pb: 1,
            px: 3,
            maxHeight: "65vh",
            overflowY: "auto",
            backgroundColor: "#F1F5F9",
          }}>
            {/* Category Details Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #2563EB",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(37,99,235,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Type size={17} color="#2563EB" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Category Details
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Category Name"
                    fullWidth
                    disabled={saving}
                    required
                    error={!!errors.name}
                    helperText={errors.name ? "Name is required" : ""}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Type size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("name", { required: true })}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Slug"
                    fullWidth
                    disabled={saving}
                    placeholder={watchName ? slugify(watchName) : "auto-generated from name"}
                    helperText="Leave blank to auto-generate. Lowercase letters, numbers, hyphens only."
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Search size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("slug")}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                    disabled={saving}
                    placeholder="Optional description of this category"
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                        <AlignLeft size={16} color="#94A3B8" />
                      </InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("description")}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                  fullWidth
                    label="Display Order"
                    type="number"
                    disabled={saving}
                    placeholder="0 = first"
                    inputProps={{ min: 0, step: 1 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Hash size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("order", { min: 0 })}
                  />
                </Box>
              </Box>
            </Paper>
          </DialogContent>

          <DialogActions sx={{
            px: 3, py: 2.25,
            borderTop: "1px solid rgba(15,23,42,0.08)",
            backgroundColor: "#FFFFFF",
            display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5,
          }}>
            <Button
              onClick={closeDialog}
              disabled={saving}
              sx={{
                textTransform: "none", fontWeight: 700, color: "#475569",
                px: 2.5, py: 1, borderRadius: "10px",
                backgroundColor: "rgba(15,23,42,0.04)",
                "&:hover": { backgroundColor: "rgba(15,23,42,0.08)" },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "10px",
                background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)",
                px: 3.5, py: 1.1, minWidth: 140,
                boxShadow: "0 4px 14px rgba(37,99,235,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1E40AF 0%, #1D4ED8 100%)",
                  boxShadow: "0 6px 18px rgba(37,99,235,0.4)"
                },
              }}
            >
              {saving ? "Saving..." : editing ? "Update Category" : "Create Category"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => !deleteMutation.isPending && setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: "1px solid rgba(15,23,42,0.08)",
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Delete this category?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            "{confirmDelete?.name}" will be deleted permanently. Resources in this category won't be deleted but will become uncategorized.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          px: 3, pb: 2.5,
          borderTop: "1px solid rgba(15,23,42,0.08)",
        }}>
          <Button
            onClick={() => setConfirmDelete(null)}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending}
            variant="contained"
            startIcon={deleteMutation.isPending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: "10px",
              bgcolor: "#EF4444", px: 2.5, minWidth: 100,
              "&:hover": { bgcolor: "#DC2626" }
            }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}